/**
 * Analytics Service - Customer Module Only, Privacy-First
 * Tracks customer behavior with privacy protection and extensible design
 */

class AnalyticsService {
  constructor() {
    this.isEnabled = true;
    this.sessionId = this.generateSessionId();
    this.anonymousUserId = this.generateAnonymousUserId(); // Privacy-safe ID
    this.userProperties = {};
    this.eventQueue = [];
    this.flushTimer = null;
    this.sessionStartTime = Date.now();
    this.pageViewCount = 0;
    this.lastActivityTime = Date.now();
    
    // Privacy & Module Configuration
    this.allowedModules = ['customer']; // Only customer module tracking
    this.currentModule = null;
    this.sensitiveFields = ['userId', 'email', 'password', 'name', 'phone', 'address']; // Blocked fields
    
    // GA4 Configuration
    this.ga4MeasurementId = process.env.REACT_APP_GA4_MEASUREMENT_ID;
    this.ga4Initialized = false;
    
    // Debug Configuration
    this.debugMode = process.env.REACT_APP_DEBUG === 'true';
    
    // Initialize GA4 and session tracking
    this.initGA4();
    this.initSessionTracking();
  }

  /**
   * Debug logging helper
   */
  debugLog(message, ...args) {
    if (this.debugMode) {
      console.log(`🔍 Analytics Debug: ${message}`, ...args);
    }
  }

  /**
   * Generate unique session ID
   */
  generateSessionId() {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Generate anonymous user ID (privacy-safe)
   */
  generateAnonymousUserId() {
    // Check if we have a stored anonymous ID
    const stored = localStorage.getItem('analytics_anonymous_id');
    if (stored) return stored;
    
    // Generate new anonymous ID
    const anonymousId = `anon_${Date.now()}_${Math.random().toString(36).substr(2, 12)}`;
    localStorage.setItem('analytics_anonymous_id', anonymousId);
    return anonymousId;
  }

  /**
   * Check if current route/module is allowed for tracking
   */
  isTrackingAllowed(path = window.location.pathname) {
    // Extract module from path
    const pathSegments = path.split('/').filter(segment => segment);
    const module = pathSegments[0] || 'customer'; // Default to customer for root paths
    
    this.currentModule = module;
    return this.allowedModules.includes(module);
  }

  /**
   * Sanitize properties to remove sensitive information
   */
  sanitizeProperties(properties) {
    const sanitized = { ...properties };
    
    // Remove sensitive fields
    this.sensitiveFields.forEach(field => {
      delete sanitized[field];
      // Also check for nested objects
      Object.keys(sanitized).forEach(key => {
        if (typeof sanitized[key] === 'object' && sanitized[key] !== null) {
          delete sanitized[key][field];
        }
      });
    });
    
    return sanitized;
  }

  /**
   * Initialize Google Analytics 4
   */
  initGA4() {
    if (!this.ga4MeasurementId || this.ga4MeasurementId === 'G-XXXXXXXXXX') {
      console.warn('GA4 Measurement ID not configured');
      return;
    }

    try {
      // Load gtag script
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${this.ga4MeasurementId}`;
      document.head.appendChild(script);

      // Initialize gtag
      window.dataLayer = window.dataLayer || [];
      window.gtag = function() {
        window.dataLayer.push(arguments);
      };

      window.gtag('js', new Date());
      window.gtag('config', this.ga4MeasurementId, {
        page_title: document.title,
        page_location: window.location.href
      });

      this.ga4Initialized = true;
      this.debugLog('GA4 initialized for customer module:', this.ga4MeasurementId);
    } catch (error) {
      console.error('Failed to initialize GA4:', error);
    }
  }

  /**
   * Initialize session tracking
   */
  initSessionTracking() {
    // Track user activity
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    activityEvents.forEach(event => {
      document.addEventListener(event, () => {
        this.lastActivityTime = Date.now();
      }, { passive: true });
    });

    // Track session duration periodically
    setInterval(() => {
      this.trackSessionHeartbeat();
    }, 30000); // Every 30 seconds

    // Track session end
    window.addEventListener('beforeunload', () => {
      this.trackSessionEnd();
    });

    // Track page visibility changes
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.trackSessionPause();
      } else {
        this.trackSessionResume();
      }
    });
  }

  /**
   * Track custom event (Customer module only, privacy-safe)
   */
  track(eventName, properties = {}) {
    this.debugLog('Track called:', eventName, properties, {
      isEnabled: this.isEnabled,
      currentPath: window.location.pathname,
      currentModule: this.currentModule
    });
    
    if (!this.isEnabled) {
      this.debugLog('Analytics disabled');
      return;
    }

    // Check if tracking is allowed for current path
    const trackingAllowed = this.isTrackingAllowed();
    this.debugLog('Tracking allowed check:', {
      path: window.location.pathname,
      module: this.currentModule,
      allowed: trackingAllowed,
      allowedModules: this.allowedModules
    });
    
    if (!trackingAllowed) {
      this.debugLog('Analytics blocked for module:', this.currentModule);
      return;
    }

    // Sanitize properties to remove sensitive data
    const sanitizedProperties = this.sanitizeProperties(properties);

    const event = {
      event: eventName,
      timestamp: Date.now(),
      sessionId: this.sessionId,
      anonymousUserId: this.anonymousUserId, // Use anonymous ID instead of real userId
      properties: {
        ...sanitizedProperties,
        ...this.userProperties,
        url: window.location.href,
        path: window.location.pathname,
        referrer: document.referrer,
        userAgent: navigator.userAgent,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        language: navigator.language,
        module: this.currentModule, // Track which module generated the event
      },
    };

    this.eventQueue.push(event);
    this.scheduleFlush();

    // Send to GA4 (privacy-safe)
    if (this.ga4Initialized && window.gtag) {
      window.gtag('event', eventName, {
        ...sanitizedProperties,
        session_id: this.sessionId,
        anonymous_user_id: this.anonymousUserId, // Use anonymous ID
        module: this.currentModule
      });
      this.debugLog('Analytics Event sent:', eventName, sanitizedProperties);
    }

  }

  /**
   * Track page view (Customer module only)
   */
  trackPageView(pageName, properties = {}) {
    this.debugLog('trackPageView called:', pageName, properties);
    
    // Check if tracking is allowed for this path
    const trackingAllowed = this.isTrackingAllowed(pageName);
    this.debugLog('Page view tracking allowed:', {
      pageName,
      allowed: trackingAllowed,
      module: this.currentModule
    });
    
    if (!trackingAllowed) {
      this.debugLog('Page view blocked for path:', pageName);
      return;
    }

    this.pageViewCount++;
    
    // Send to GA4
    if (this.ga4Initialized && window.gtag) {
      window.gtag('config', this.ga4MeasurementId, {
        page_path: pageName,
        page_title: document.title,
        page_location: window.location.origin + pageName
      });
    }
    
    this.track('page_view', {
      pageName,
      pageTitle: document.title,
      pageViewNumber: this.pageViewCount,
      ...properties,
    });
  }

  // === CUSTOMER-SPECIFIC ECOMMERCE TRACKING ===
  
  /**
   * Track product view (Customer only)
   */
  trackProductView(productId, productName, category, price) {
    this.track('view_item', {
      currency: 'INR',
      value: price,
      items: [{
        item_id: productId,
        item_name: productName,
        item_category: category,
        price: price,
        quantity: 1
      }]
    });
  }

  /**
   * Track add to cart (Customer only)
   */
  trackAddToCart(productId, productName, category, price, quantity = 1) {
    this.track('add_to_cart', {
      currency: 'INR',
      value: price * quantity,
      items: [{
        item_id: productId,
        item_name: productName,
        item_category: category,
        price: price,
        quantity: quantity
      }]
    });
  }

  /**
   * Track remove from cart (Customer only)
   */
  trackRemoveFromCart(productId, productName, category, price, quantity = 1) {
    this.track('remove_from_cart', {
      currency: 'INR',
      value: price * quantity,
      items: [{
        item_id: productId,
        item_name: productName,
        item_category: category,
        price: price,
        quantity: quantity
      }]
    });
  }

  /**
   * Track begin checkout (Customer only)
   */
  trackBeginCheckout(cartValue, items) {
    this.track('begin_checkout', {
      currency: 'INR',
      value: cartValue,
      items: items.map(item => ({
        item_id: item.id,
        item_name: item.name,
        item_category: item.category,
        price: item.price,
        quantity: item.quantity
      }))
    });
  }

  /**
   * Track purchase (Customer only)
   */
  trackPurchase(orderId, orderValue, items) {
    this.track('purchase', {
      transaction_id: orderId,
      currency: 'INR',
      value: orderValue,
      items: items ? items.map(item => ({
        item_id: item.id,
        item_name: item.name,
        item_category: item.category,
        price: item.price,
        quantity: item.quantity,
      })) : [],
    });
  }

  /**
   * Track search (Customer only)
   */
  trackSearch(searchTerm, resultsCount = null) {
    this.track('search', {
      search_term: searchTerm,
      ...(resultsCount !== null && { results_count: resultsCount })
    });
  }

  /**
   * Track customer registration (Privacy-safe)
   */
  trackCustomerRegistration(method = 'email') {
    this.track('sign_up', {
      method: method
    });
  }

  /**
   * Track customer login (Privacy-safe)
   */
  trackCustomerLogin(method = 'email') {
    this.track('login', {
      method: method
    });
  }

  /**
   * Track application errors (Privacy-safe)
   */
  trackError(error, errorInfo = {}) {
    this.track('error', {
      error_message: error?.message || 'Unknown error',
      error_name: error?.name || 'Error',
      error_stack: error?.stack ? error.stack.substring(0, 500) : null, // Limit stack trace length
      component_stack: errorInfo?.componentStack ? errorInfo.componentStack.substring(0, 500) : null,
      error_boundary: true,
      timestamp: new Date().toISOString()
    });
  }

  // === SESSION TRACKING ===

  trackSessionHeartbeat() {
    const sessionDuration = Date.now() - this.sessionStartTime;
    const timeSinceLastActivity = Date.now() - this.lastActivityTime;
    
    // Only track if user is still active (within 5 minutes)
    if (timeSinceLastActivity < 300000) {
      this.track('session_heartbeat', {
        sessionDuration,
        timeSinceLastActivity,
      });
    }
  }

  trackSessionEnd() {
    const sessionDuration = Date.now() - this.sessionStartTime;
    this.track('session_end', {
      sessionDuration,
      pageViewCount: this.pageViewCount,
    });
  }

  trackSessionPause() {
    this.track('session_pause', {
      sessionDuration: Date.now() - this.sessionStartTime,
    });
  }

  trackSessionResume() {
    this.track('session_resume', {
      sessionDuration: Date.now() - this.sessionStartTime,
    });
  }

  // === UTILITY METHODS ===

  scheduleFlush() {
    if (this.flushTimer) return;
    
    this.flushTimer = setTimeout(() => {
      this.flushEvents();
      this.flushTimer = null;
    }, 1000);
  }

  flushEvents() {
    if (this.eventQueue.length === 0) return;
    
    // In a real implementation, you might send events to your own analytics endpoint
    // For now, we rely on GA4 for data collection
    
    
    this.eventQueue = [];
  }

  // === EXTENSIBILITY METHODS ===

  /**
   * Add allowed module for tracking (extensible)
   */
  addAllowedModule(moduleName) {
    if (!this.allowedModules.includes(moduleName)) {
      this.allowedModules.push(moduleName);
    }
  }

  /**
   * Remove allowed module (extensible)
   */
  removeAllowedModule(moduleName) {
    this.allowedModules = this.allowedModules.filter(module => module !== moduleName);
  }

  /**
   * Add sensitive field to block list (extensible)
   */
  addSensitiveField(fieldName) {
    if (!this.sensitiveFields.includes(fieldName)) {
      this.sensitiveFields.push(fieldName);
    }
  }

  /**
   * Enable/disable analytics
   */
  setEnabled(enabled) {
    this.isEnabled = enabled;
  }
}

// Export singleton instance
const analyticsService = new AnalyticsService();
export default analyticsService;
